import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthFrame } from '@/components/AuthFrame';
import { authClient } from '@/lib/auth-client';
import { errorText } from '@/lib/errors';
import { client } from '@/lib/orpc';

export const Route = createFileRoute('/invite')({ component: InvitePage });

/** The secret travels in the #fragment: never sent to a server, never in an access log. */
function InvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = window.location.hash.slice(1);
  const invite = useQuery({ queryKey: ['invite', token], enabled: !!token, retry: false, queryFn: () => client.invites.inspect({ token }) });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const accept = useMutation({
    mutationFn: async () => {
      const { email } = await client.invites.accept({ token, name, password });
      const { error } = await authClient.signIn.email({ email, password });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => navigate({ to: '/setup-passkey', replace: true })
  });

  if (!token || invite.isError) {
    return (
      <AuthFrame title={t('auth.inviteTitle')}>
        <p className="text-error text-sm">{t('auth.inviteInvalid')}</p>
      </AuthFrame>
    );
  }
  if (!invite.data) {
    return (
      <AuthFrame title={t('auth.inviteTitle')}>
        <span className="loading loading-spinner" />
      </AuthFrame>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    accept.mutate();
  };
  return (
    <AuthFrame title={t('auth.inviteTitle')} lead={t('auth.inviteLead', { email: invite.data.email })}>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm">{t('auth.name')}</span>
          <input className="input w-full" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm">{t('auth.password')}</span>
          <input className="input w-full" type="password" autoComplete="new-password" minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <span className="text-base-content/60 text-xs">{t('auth.passwordHint')}</span>
        </label>
        {accept.isError && <p className="text-error text-sm">{errorText(t, accept.error)}</p>}
        <button type="submit" className="btn btn-primary w-full" disabled={accept.isPending}>
          {t('auth.acceptInvite')}
        </button>
      </form>
    </AuthFrame>
  );
}
